<script lang="ts" setup>
import { watch } from 'vue'

import { useCurrentProviderName } from '@/composables/useCurrentProviderName'
import { resolveProviderUuid } from '@/composables/useProviderAccess'
import UserProfile from '@/layouts/components/UserProfile.vue'
import { useProviderNavItems } from '@/navigation/provider'
import { useIvProviderStore } from '@/stores/insurance/provider'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import logo from '@images/logos/vector.svg'
import { VerticalNavLayout } from '@layouts'

const providerStore = useIvProviderStore()
const currentProviderName = useCurrentProviderName()
const { appRouteTransition, isLessThanOverlayNavBreakpoint } = useThemeConfig()
const { width: windowWidth } = useWindowSize()
const { navItems } = useProviderNavItems()

// Insurance workspace routes share one component and must stay mounted when only the
// session UUID (or, since VH-2357, the patient/plan_uuid) changes — keying by full path
// remounted the page on every activity click and flashed the empty "Patient Insurance
// Lookup" form. iv-plan-workspace is the durable plan_uuid route this same bug reappeared
// on when it was added without also adding it here.
//
// iv-verify/iv-workspace/iv-discovery are gone — VH-2263 replaced them with iv-lookup (entry
// only, its own component) and VH-2357's iv-plan-workspace is the sole result view. Only the
// latter changes params without changing component, so it's the only one that needs a
// param-independent key; iv-lookup takes no params and is fine on plain path keying.
const INSURANCE_WORKSPACE_ROUTES = new Set(['iv-plan-workspace'])

function viewKey(route: { name?: string | symbol | null; path: string; params: Record<string, unknown> }) {
  const name = String(route.name ?? '')
  if (INSURANCE_WORKSPACE_ROUTES.has(name))
    return `iv-workspace-${route.params.provider_uuid}`

  return route.path
}

// Settings are keyed to whichever provider resolveProviderUuid() currently points
// at (see providerBase()), but this layout stays mounted across navigations
// between different providers' routes (e.g. a Vector admin opening another
// provider) — refetch whenever the resolved uuid changes rather than only once on
// mount, or `providerStore.settings` (and the navbar chip's fallback name) goes
// stale for the previous provider.
watch(resolveProviderUuid, async (uuid, previousUuid) => {
  if (uuid && uuid !== previousUuid)
    await providerStore.fetchSettings().catch(_e => _e)
}, { immediate: true })
</script>

<template>
  <div class="provider-layout-root">
    <VerticalNavLayout :nav-items="navItems">
      <!-- 👉 Suppress the sidebar's own header logo — logo lives in the top navbar instead -->
      <template #vertical-nav-header />

      <template #navbar="{ toggleVerticalOverlayNavActive }">
        <div class="d-flex h-100 align-center">
          <IconBtn
            v-if="isLessThanOverlayNavBreakpoint(windowWidth)"
            id="vertical-nav-toggle-btn"
            class="ms-n3"
            @click="toggleVerticalOverlayNavActive(true)"
          >
            <VIcon icon="mdi-menu" />
          </IconBtn>

          <RouterLink class="d-flex align-center gap-x-3" to="/">
            <VImg :src="logo" height="22" width="155" />
          </RouterLink>

          <VChip
            v-if="currentProviderName"
            class="ms-3"
            size="small"
            variant="tonal"
            color="primary"
          >
            {{ currentProviderName }}
          </VChip>

          <VSpacer />
          <UserProfile />
        </div>
      </template>

      <RouterView v-slot="{ Component, route }">
        <Transition :name="appRouteTransition || ''" appear>
          <Component :is="Component" :key="viewKey(route)" />
        </Transition>
      </RouterView>
    </VerticalNavLayout>
  </div>
</template>

<style>
/*
  Only routes that opt into the fixed-height workspace treatment (via
  `layoutWrapperClasses: ['layout-content-height-fixed']` in the route meta —
  currently iv-lookup/iv-bulk/iv-price-list/
  provider-settings) get this no-padding, full-width, hidden-overflow
  treatment. Applying it unconditionally to every provider route broke
  scrolling on pages that don't provide their own bounded internal scroll
  container (VH-1817 regression) — other provider pages fall back to the
  default `.layout-page-content` behavior (natural padding/scroll) instead.

  VerticalNavLayout wraps the page in `.page-content-container`; that node must
  participate in the flex height chain or the workspace root never gets a
  bounded height — overflow:hidden then clips the chat composer and nothing
  scrolls (post VH-1817 horizontal→vertical regression).
*/
.provider-layout-root {
  display: flex;
  flex-direction: column;
  block-size: 100%;
  min-block-size: calc(var(--vh, 1vh) * 100);
}

.provider-layout-root .layout-wrapper {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-block-size: 0;
}

.provider-layout-root .layout-wrapper.layout-content-height-fixed .layout-content-wrapper {
  overflow: hidden;
}

.provider-layout-root .layout-wrapper.layout-content-height-fixed .layout-page-content {
  display: flex;
  overflow: hidden;
  flex: 1;
  flex-direction: column;
  padding: 0 !important;
  margin-inline: 0 !important;
  max-inline-size: 100% !important;
  min-block-size: 0;
}

.provider-layout-root .layout-wrapper.layout-content-height-fixed .page-content-container {
  position: relative;
  display: flex;
  overflow: hidden;
  flex: 1;
  flex-direction: column;
  inline-size: 100%;
  min-block-size: 0;
}

.provider-layout-root .layout-wrapper.layout-content-height-fixed .page-content-container > * {
  position: absolute;
  overflow: hidden;
  inset: 0;
  max-block-size: none;
}
</style>
